import { inject, Injectable, signal, WritableSignal } from "@angular/core";

import { LogService } from "../../../../../../libs/common/src/abstractions/log.service";
import { AdState, StoredAdData } from "../../models/interfaces/credits.interfaces";
import { DEFAULT_AD_STATES } from "../../models/constants/credits.constants";

@Injectable({
  providedIn: "root",
})
export class AdStateService {
  private logService: LogService = inject(LogService);
  timerIntervals: any[] = [];
  private readonly STORAGE_KEY = "gloss_ad_state";
  adStates: WritableSignal<AdState[]> = signal<AdState[]>([]);
  maxAdsPerDay = 5;
  adsWatchedToday = 0;

  loadStateFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data: StoredAdData = JSON.parse(stored);
        this.adStates.set(data.adStates);
        this.adsWatchedToday = data.adsWatchedToday;

        // Clear existing timers before restarting
        this.timerIntervals.forEach((interval) => clearInterval(interval));
        this.timerIntervals = [];

        // Restart any active timers
        this.restartActiveTimers();
      } else {
        this.adStates.set(DEFAULT_AD_STATES);
      }
    } catch (error) {
      this.logService.error("Error loading ad state from storage: " + error);
      this.resetAdState();
    }
  }

  clearStuckLoadingStates(): boolean {
    const current = this.adStates();
    const loadingAds = current.filter((ad) => ad.isLoading);
    if (loadingAds.length > 0) {
      const cleaned = current.map((ad) => ({ ...ad, isLoading: false }));
      this.adStates.set(cleaned);
      this.saveStateToStorage();
      return true; // Return true because we performed a cleanup
    }
    return false;
  }

  private restartActiveTimers(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      const data: StoredAdData = JSON.parse(stored);
      const lastResetDate = data.lastResetDate;
      // Reset if it's a new day
      if (!this.isToday(lastResetDate)) {
        this.restartAdTimers();
        return;
      }
    }

    this.adStates().forEach((ad, index) => {
      if (index === 0) {
        return;
      } // Skip first ad

      const previousAd = this.adStates()[index - 1];

      if (ad.locked && !ad.watched && previousAd.watchedAt) {
        const elapsedSeconds = Math.floor((Date.now() - previousAd.watchedAt) / 1000);
        const requiredSeconds = 30 * 60; // 30 minutes
        const remainingSeconds = requiredSeconds - elapsedSeconds;

        if (remainingSeconds <= 0) {
          // More than 30 minutes have passed, unlock immediately
          ad.locked = false;
          ad.timeRemaining = 0;
        } else {
          // Update time remaining and start timer
          ad.timeRemaining = remainingSeconds;
          this.startTimerForAd(index, true);
        }
      }
    });
    // Save the updated state after checking all ads
    this.saveStateToStorage();
  }

  isToday(dateString: string): boolean {
    const today = this.getTodayDateString();
    return dateString === today;
  }

  restartAdTimers(): void {
    this.resetAdState();
    this.saveStateToStorage();
  }

  startTimerForAd(adIndex: number, preserveTime: boolean = false): void {
    const currentAdStates = this.adStates();
    const ad = currentAdStates[adIndex];
    const previousAd = currentAdStates[adIndex - 1];

    if (!previousAd || !previousAd.watchedAt) {
      this.logService.error("Cannot start timer: previous ad has no watchedAt timestamp");
      return;
    }

    // Calculate initial time remaining
    const requiredSeconds = 30 * 60; // 30 minutes
    const elapsedSeconds = Math.floor((Date.now() - previousAd.watchedAt) / 1000);
    const remainingSeconds = requiredSeconds - elapsedSeconds;

    if (remainingSeconds <= 0) {
      // More than 30 minutes have passed, unlock immediately
      const updatedStates = [...currentAdStates];
      updatedStates[adIndex] = { ...ad, locked: false, timeRemaining: 0 };
      this.adStates.set(updatedStates);
      this.saveStateToStorage();
      return;
    }

    // Set initial time remaining
    const updatedStates = [...currentAdStates];
    updatedStates[adIndex] = { ...updatedStates[adIndex], timeRemaining: remainingSeconds };
    this.adStates.set(updatedStates);

    const interval = setInterval(() => {
      // Recalculate time remaining based on elapsed time since watchedAt
      const elapsed = Math.floor((Date.now() - previousAd.watchedAt) / 1000);
      const remaining = requiredSeconds - elapsed;

      const currentStates = this.adStates();
      const updatedAd = { ...currentStates[adIndex], timeRemaining: Math.max(0, remaining) };

      if (remaining <= 0) {
        updatedAd.locked = false;
        updatedAd.timeRemaining = 0;
        const newStates = [...currentStates];
        newStates[adIndex] = updatedAd;
        this.adStates.set(newStates);
        clearInterval(interval);
        const intervalIndex = this.timerIntervals.indexOf(interval);
        if (intervalIndex > -1) {
          this.timerIntervals.splice(intervalIndex, 1);
        }
        this.saveStateToStorage();
      } else {
        // Update time remaining every second
        const newStates = [...currentStates];
        newStates[adIndex] = updatedAd;
        this.adStates.set(newStates);

        // Save state periodically (every minute)
        if (remaining % 60 === 0) {
          this.saveStateToStorage();
        }
      }
    }, 1000);

    this.timerIntervals.push(interval);
  }

  saveStateToStorage(): void {
    try {
      const data: StoredAdData = {
        adStates: this.adStates(),
        adsWatchedToday: this.adsWatchedToday,
        lastResetDate: this.getTodayDateString(),
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      this.logService.error("Error saving ad state to storage: " + error);
    }
  }

  private getTodayDateString(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`; // Returns YYYY-MM-DD in local timezone
  }

  resetAdState(): void {
    this.adsWatchedToday = 0;
    // Initialize with 5 ads for first-time users
    this.adStates.set(DEFAULT_AD_STATES);

    // Clear any existing timers
    this.timerIntervals.forEach((interval) => clearInterval(interval));
    this.timerIntervals = [];
  }

  checkAndResetDaily(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data: StoredAdData = JSON.parse(stored);
        const lastResetDate = data.lastResetDate;
        // Reset if it's a new day
        if (!this.isToday(lastResetDate)) {
          this.restartAdTimers();
        }
      }
    } catch (error) {
      this.logService.error("Error in checkAndResetDaily: " + error);
      this.resetAdState();
    }
  }
}
